(() => {
  if (customElements.get('animated-globe')) {
    return;
  }

  /**
   * Animated globe component.
   */
  class AnimatedGlobe extends HTMLElement {
    /**
     * Constructor.
     */
    constructor() {
      super();
      this.globeValues = {
        initialRotation: 180,
        initialScale: 0.2,
        finalRotation: 0,
        finalScale: 1
      }
    }

    /**
     * Check scroll position.
     */
    checkScrollPosition() {
      const globeValues = this.getGlobeRotationAndScale();
      if (areObjectsEqual(globeValues, this.getInitialGlobeValues())) {
        this.classList.remove('is-fully-visible');
        this.scheduledAnimationFrame = false;
        return;
      }

      this.classList.toggle(
        'is-fully-visible', 
        areObjectsEqual(globeValues, this.getFinalGlobeValues())
      );
      
      this.globe.style.setProperty('--globe-rotation', `${globeValues.rotation}deg`);
      this.globe.style.setProperty('--globe-scale', `${globeValues.scale}`);
      this.globe.style.setProperty(
        '--globe-opacity', 
        `${globeValues.scale < 0.8 ? globeValues.scale * 0.5 : globeValues.scale}`
      );
      
      this.scheduledAnimationFrame = false;
    }

    /**
     * Get initial globe values.
     * 
     * @returns {Object<rotation: Number, scale: Number>} - GlobeValues
     */
    getInitialGlobeValues() {
      return {
        rotation: this.globeValues.initialRotation,
        scale: this.globeValues.initial
      }
    }

    /**
     * Get final globe values.
     * 
     * @returns {Object<rotation: Number, scale: Number>} - GlobeValues
     */
    getFinalGlobeValues() {
      return {
        rotation: this.globeValues.finalRotation,
        scale: this.globeValues.finalScale
      }
    }

    /**
     * Get globe rotation and scale.
     * 
     * @returns {Object<rotation: Number, scale: Number>} - GlobeValues
     */
    getGlobeRotationAndScale() {
      const topOffset = this.offsetTop;
      const animationStart = topOffset - this.globe.clientHeight * this.getViewportOffset();
      if (window.scrollY < animationStart) {
        return this.getInitialGlobeValues();
      }

      const percentDifference =  (window.scrollY - animationStart) / this.globe.clientHeight;
      if (percentDifference > 1) {
        return this.getFinalGlobeValues();
      }
      if (percentDifference < 0.2) {
        return this.getInitialGlobeValues();
      }

      const rotateDifference = this.globeValues.finalRotation - this.globeValues.initialRotation;
      const rotate = this.globeValues.initialRotation + rotateDifference * percentDifference;
      return {
        rotation: rotate,
        scale: percentDifference
      }
    }

    /**
     * Get viewport offset.
     * 
     * @returns {Number} - Offset
     */
    getViewportOffset() {
      const largeDesktopMatchQuery = window.matchMedia('(min-width: 1800px)');
      const desktopMatchQuery = window.matchMedia('(min-width: 1600px)');
      const smallDesktopMatchQuery = window.matchMedia('(min-width: 1024px)');
      const tabletMatchQuery = window.matchMedia('(min-width: 768px)');
      if (largeDesktopMatchQuery.matches) {
        return 1.5;
      }
      
      if (desktopMatchQuery.matches) {
        return 2.1;
      }

      if (smallDesktopMatchQuery.matches) {
        return 2.5;
      }
      
      if (tabletMatchQuery.matches) {
        return 4;
      }

      return 6;
    }
    
    /**
     * On scroll and resize.
     *
     * @returns {Void}
     */
    onScrollAndResize() {
      if (this.scheduledAnimationFrame) {
        return;
      }

      this.scheduledAnimationFrame = true;
      window.requestAnimationFrame(this.checkScrollPosition.bind(this));
    }
    
    /**
     * Connected callback.
     */
    connectedCallback() {
      this.globe = this.querySelector("[data-globe]");
      if (!this.globe) {
        return;
      }
      this.checkScrollPosition();
      this.setHandlers();
    }

    /**
     * Set handlers.
     */
    setHandlers() {
      ["scroll", "resize"].forEach((event) => {
        window.addEventListener(event, this.onScrollAndResize.bind(this));
      });
    }
  }

  customElements.define('animated-globe', AnimatedGlobe);
})();